//
// # usePropertiesDescriptor
//
// Derives the current selection's `EntityDescriptor`. Synchronous fields come
// from `workspace.properties.describeSelection()` (re-derived on every snapshot bump —
// selectionVersion / contentVersion). Async generator params (`params` /
// `paramSpec`) for instance / layoutInstance kinds are fetched via the
// `get-instance` endpoint and merged in, with `paramsLoading` reflecting the
// in-flight state. The fetch re-runs on entity identity change or contentVersion
// bump (external edit), matching the documented re-derive behavior.
//

import { useEffect, useRef, useState } from "react";
import { Ident } from "GenAlphaModel";
import type { GeneratorParamSpec } from "ServerInterface";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import type { EntityDescriptor } from "../../types";

interface FetchedParams {
  readonly key: string;
  readonly params?: Record<string, unknown>;
  readonly paramSpec?: GeneratorParamSpec;
}

// Read a property off an object without type assertions (lint-forbidden).
// `Reflect.get` returns `any`; the `unknown` return type launders it so no
// `any` escapes into the caller.
function readKey(value: object, key: string): unknown {
  return Reflect.get(value, key);
}

// Order-independent structural equality. Used to decide whether the merged
// descriptor's *content* actually changed (vs. just its object identity, which
// is fresh on every render). This replaces a `JSON.stringify` signature, which
// is sensitive to key order and pays a full-serialization cost every render;
// this instead short-circuits on the first difference (usually `kind` / `id`).
// Arrays are compared via `Object.keys` index keys, so a single object path
// covers both — with an `Array.isArray` cross-check so an array never compares
// equal to a plain object that happens to share the same keys.
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || a === null) return false;
  if (typeof b !== "object" || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!deepEqual(readKey(a, k), readKey(b, k))) return false;
  }
  return true;
}

// Descriptor fields that are large/deep (a generator's param dict and spec) but
// carry a *stable identity* — they only ever come from the `fetched` state
// (see the merge below), so a new object means new content and an unchanged
// reference means unchanged content. Comparing them by reference avoids
// recursing the (potentially large) `paramSpec` on every render.
const REFERENCE_COMPARED_KEYS = new Set(["params", "paramSpec"]);

// Whether two merged descriptors have equal *content*. Everything is tiny
// (primitives + `{x,y}`/`{min,max}`) and deep-compared, EXCEPT `params` /
// `paramSpec`, which are reference-compared (see REFERENCE_COMPARED_KEYS). This
// keeps the precise per-entity semantics of `deepEqual` (an unrelated edit that
// leaves the selected entity's content unchanged keeps the stable identity, so
// in-progress drafts aren't discarded) without ever walking the large objects.
function descriptorEqual(
  a: EntityDescriptor | null,
  b: EntityDescriptor | null
): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    const av = readKey(a, k);
    const bv = readKey(b, k);
    if (REFERENCE_COMPARED_KEYS.has(k)) {
      if (av !== bv) return false;
    } else if (!deepEqual(av, bv)) {
      return false;
    }
  }
  return true;
}

// Stable identity key for the params fetch (null when params don't apply).
function paramKey(descriptor: EntityDescriptor | null): string | null {
  if (descriptor === null) return null;
  if (descriptor.kind !== "instance" && descriptor.kind !== "layoutInstance") {
    return null;
  }
  return `${Ident.stringify(descriptor.designIdent)}:${descriptor.kind}:${
    descriptor.id
  }`;
}

export function usePropertiesDescriptor(): EntityDescriptor | null {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const contentVersion = snapshot.contentVersion;

  // Synchronous descriptor — re-derived on every render (the snapshot
  // subscription re-renders on selectionVersion / contentVersion bumps).
  const base = workspace.properties.describeSelection();

  // Stable identity key for the fetch effect (avoid depending on `base`, which
  // is a fresh object each render).
  const key = paramKey(base);

  const [fetched, setFetched] = useState<FetchedParams | null>(null);

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    // Re-read the live descriptor inside the effect to obtain the current
    // entity's ident + id (so we depend only on the stable `key`, not the
    // fresh descriptor object).
    const live = workspace.properties.describeSelection();
    if (
      live === null ||
      (live.kind !== "instance" && live.kind !== "layoutInstance")
    ) {
      return;
    }
    void workspace.properties
      .getInstanceDetails(live.designIdent, live.id)
      .then((details) => {
        if (cancelled || details === null) return;
        setFetched({
          key,
          params: details.params,
          paramSpec: details.paramSpec,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [key, contentVersion, workspace]);

  // Compute the merged descriptor (a fresh object each render).
  let merged: EntityDescriptor | null;
  if (base === null) {
    merged = null;
  } else if (base.kind !== "instance" && base.kind !== "layoutInstance") {
    merged = base;
  } else if (fetched !== null && fetched.key === key) {
    // Merge fetched params if they belong to the current entity.
    merged = {
      ...base,
      params: fetched.params,
      paramSpec: fetched.paramSpec,
      paramsLoading: false,
    };
  } else {
    // Still loading (or params not yet fetched for this entity).
    merged = { ...base, paramsLoading: true };
  }

  // Stabilize identity. `describeSelection()` returns a fresh object on every
  // render, so consumers that depend on the descriptor object (e.g. the draft
  // re-seed effect in PropertiesPanel) would re-run every render and loop
  // ("Maximum update depth exceeded"). Return a new reference only when the
  // descriptor's *content* actually changes (selection / external edit /
  // params finishing loading), determined by a structural deep comparison
  // against the last returned value.
  const stable = useRef<EntityDescriptor | null>(merged);
  if (!descriptorEqual(stable.current, merged)) {
    stable.current = merged;
  }
  return stable.current;
}
